import invariant from "invariant";
import { APIError } from "./utils";

// List of HTTP Status code that does not trigger an error
const DEFAULT_EXPECTED = [200, 201, 202, 203, 204, 205];

/* Encode connector, it encodes the body as JSON if required */
export function withEncode(fetch) {
	return (url, options={}) => {
		if (options.data && typeof options.data == 'object') {
			options.headers = options.headers || {};
			options.headers["Content-Type"] = "application/json";
			options.body = JSON.stringify(options.data);
			delete options.data;
		}
		return fetch(url, options);
	};
}

/* Encode connector, it raises APIError if the response fails */
export function withAPIError(fetch) {
	return async (url, options) => {
		invariant(options.expected === undefined || options.expected instanceof Array, 'options.expected must be absent or an array');
		const expected = options.expected || DEFAULT_EXPECTED;
		let response = await fetch(url, options);

		if (response.status === 502) {
			response = await fetch(url, options);
		}
		// status code is not ok and is not expected
		if (!expected.includes(response.status)) {
			const value = await response.json().catch(() => null);
			throw new APIError(`${options.method || 'GET' } ${url} returned ${response.status}`, response, value);
		}
		return response;
	};
}

function getDataFromResponse(response) {
	const contentType = response.headers.get('Content-Type') || '';
	if (contentType.indexOf('application/json') === -1) {
		return null;
	}
	return response.json();
}

/* APIMiddleware is a redux middleware that intercepts some action types, that
 * are meant to do XHR requests. It intercepts the action and dispatches it 2
 * times. If the request succeeds, it dispatches the action first with the XHR
 * Promise as payload.request and a second time with the resolved value. If the
 * request fails, it's dispatched the first time exactly as if it succeeeded and
 * another time with the error in the meta key.
 *
 * The request is marked as a failure if the response ok flag is false.
 */
function makeDispatchFetch(fetch, store) {

	return async (action) => {
		const { url, fetchOptions={}, ...options } = action.request;
		// AbortController is used to abort pending requests
		const abortController = new AbortController();
		options.signal = abortController.signal;
		// Include the cookies in the CORS requests
		options.credentials = 'include';

		const request = fetch(url, {...options, ...fetchOptions});

		store.dispatch({
			...action,
			success: null,
			request: request,
			abort: () => abortController.abort(),
		});

		let response = null;
		try {
			response = await request;
			const data = await getDataFromResponse(response);
			store.dispatch({
				...action,
				success: true,
				request: request,
				response: response,
				data,
			});
		} catch (err) {
			if (err instanceof APIError) {
				response = err.response;
			}
			store.dispatch({
				...action,
				success: false,
				request: request,
				response: response,
				meta: err,
			});
		};
	};
}

export function APIMiddleware(fetch=global.fetch, connectors) {
	connectors = connectors || APIMiddleware.connectors;
	return (store) => {
		// decorate fetch with all the connectors
		fetch = connectors.reverse().reduce((f, connector) => connector(f, store), fetch);

		const dispatchFetch = makeDispatchFetch(fetch, store);
		return (next) => (action) => {
			if (action.request && action.success === undefined) {
				// intercept action.request and feed them to fetch.
				return dispatchFetch(action);
			}
			return next(action);
		};
	};
};

APIMiddleware.connectors = [
	withAPIError,
	withEncode,
];
